import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, ArrowLeft, CheckCircle, PackageCheck, RotateCcw } from 'lucide-react';

import DateSelector from '../masivo/DateSelector';
import ValidationProgress from '../masivo/ValidationProgress';
import { parseExcelFile, PROVINCE_TEMPLATE_COLUMNS } from '../../../utils/provinceExcelParser';
import {
  PROVINCE_REQUIRED_COLUMNS,
  provinceDeliveryToApi,
  validateProvinceDelivery,
} from '../../../utils/provinceDeliveryValidator';
import AtomicSubmissionProgress from './AtomicSubmissionProgress';
import ProvinceDeliveriesReview from './ProvinceDeliveriesReview';
import ProvinceFileUploader from './ProvinceFileUploader';


const API_URL = process.env.REACT_APP_API_URL;
const MAX_ROWS = 200;

function createCorrelationId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `province-bulk-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function ProvinciaMasivoWizard({ userId, setActiveComponent }) {
  const [currentStep, setCurrentStep] = useState('date');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [userProducts, setUserProducts] = useState([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);
  const [validationProgress, setValidationProgress] = useState({ total: 0, processed: 0 });
  const [submissionResult, setSubmissionResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    async function loadCatalogs() {
      setLoadingCatalogs(true);
      try {
        const [provinceResponse, productResponse] = await Promise.all([
          axios.get(`${API_URL}/api/catalog/provinces`),
          axios.get(`${API_URL}/api/masivo/user-products/${userId}`),
        ]);
        if (!active) return;
        setProvinces(provinceResponse.data.provinces || []);
        setUserProducts(productResponse.data.products || []);
      } catch (catalogError) {
        console.error('Error loading province bulk catalogs:', catalogError);
        if (active) setError('No se pudieron cargar las provincias o los productos. Intenta nuevamente.');
      } finally {
        if (active) setLoadingCatalogs(false);
      }
    }
    loadCatalogs();
    return () => { active = false; };
  }, [userId]);

  function handleDateChange(date) {
    setSelectedDate(date);
    setSelectedFile(null);
    setDeliveries([]);
    setSubmissionResult(null);
    setError('');
    setCurrentStep(date ? 'upload' : 'date');
  }

  function clearFile() {
    setSelectedFile(null);
    setDeliveries([]);
    setValidationProgress({ total: 0, processed: 0 });
    setError('');
    setCurrentStep('upload');
  }

  async function handleFileSelect(file) {
    setSelectedFile(file);
    setDeliveries([]);
    setError('');
    setCurrentStep('validating');

    try {
      const parsed = await parseExcelFile(file);
      if (parsed.rows.length === 0) throw new Error('El archivo Excel está vacío');
      if (parsed.rows.length > MAX_ROWS) throw new Error(`El archivo supera el máximo de ${MAX_ROWS} entregas`);

      const headers = parsed.headers.map(header => String(header || '').trim());
      const missingHeaders = PROVINCE_REQUIRED_COLUMNS.filter(header => !headers.includes(header));
      if (missingHeaders.length > 0) {
        throw new Error(`Faltan columnas obligatorias: ${missingHeaders.join(', ')}`);
      }
      const recognizedHeaders = headers.filter(header => PROVINCE_TEMPLATE_COLUMNS.includes(header));
      if (recognizedHeaders.length === 0) throw new Error('El archivo no corresponde a la plantilla de provincia');

      setValidationProgress({ total: parsed.rows.length, processed: 0 });
      const validated = [];
      for (let index = 0; index < parsed.rows.length; index += 1) {
        validated.push(validateProvinceDelivery(parsed.rows[index], index + 2, provinces, userProducts));
        setValidationProgress({ total: parsed.rows.length, processed: index + 1 });
        if ((index + 1) % 20 === 0) {
          await new Promise(resolve => window.setTimeout(resolve, 0));
        }
      }
      setDeliveries(validated);
      setCurrentStep('review');
    } catch (fileError) {
      console.error('Error validating province bulk file:', fileError);
      setError(fileError.message || 'No se pudo procesar el archivo');
      setCurrentStep('upload');
    }
  }

  function updateDelivery(updated) {
    setDeliveries(previous => previous.map(delivery => (
      delivery.rowNumber === updated.rowNumber ? updated : delivery
    )));
  }

  function deleteDelivery(rowNumber) {
    if (!window.confirm(`¿Eliminar la fila ${rowNumber} del lote?`)) return;
    setDeliveries(previous => previous.filter(delivery => delivery.rowNumber !== rowNumber));
  }

  async function submitBatch() {
    const invalid = deliveries.filter(delivery => !delivery.isValid);
    if (invalid.length > 0 || deliveries.length === 0) return;

    setError('');
    setCurrentStep('submitting');
    const correlationId = createCorrelationId();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/delivery/provincia/bulk`,
        {
          user_id: userId,
          delivery_date: selectedDate,
          source_file_name: selectedFile?.name || '',
          deliveries: deliveries.map(provinceDeliveryToApi),
        },
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'X-Correlation-ID': correlationId,
          },
        },
      );
      setSubmissionResult(response.data);
      setCurrentStep('complete');
    } catch (submissionError) {
      console.error('Error creating province bulk batch:', submissionError);
      const validationErrors = submissionError.response?.data?.validation_errors || [];
      if (validationErrors.length > 0) {
        setDeliveries(previous => previous.map((delivery) => {
          const serverErrors = validationErrors
            .filter(item => Number(item.row) === delivery.rowNumber)
            .map(item => ({ field: item.field || 'server', message: item.message }));
          if (serverErrors.length === 0) return delivery;
          return {
            ...delivery,
            errors: [...delivery.errors, ...serverErrors],
            isValid: false,
          };
        }));
      }
      const detail = validationErrors.slice(0, 5).map(item => `Fila ${item.row || '-'}: ${item.message}`).join(' · ');
      setError(detail || submissionError.response?.data?.error || 'No se pudo crear el lote de provincia');
      setCurrentStep('review');
    }
  }

  function reset() {
    setSelectedDate('');
    setSelectedFile(null);
    setDeliveries([]);
    setSubmissionResult(null);
    setValidationProgress({ total: 0, processed: 0 });
    setError('');
    setCurrentStep('date');
  }

  function goBack() {
    if (currentStep === 'date') {
      setActiveComponent('EntregaProvincia');
      return;
    }
    if (currentStep === 'complete') {
      setActiveComponent('Analytics');
      return;
    }
    setSelectedFile(null);
    setDeliveries([]);
    setError('');
    setCurrentStep('date');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <header className="border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto max-w-5xl px-4 py-4 sm:px-6">
          <button type="button" onClick={goBack} disabled={currentStep === 'submitting'} className="flex items-center gap-2 font-montserrat text-sm font-semibold text-slate-700 hover:text-brand-primary disabled:opacity-50">
            <ArrowLeft className="h-5 w-5" />
            {currentStep === 'date' ? 'Volver a entrega individual' : 'Volver'}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        <div>
          <h1 className="font-montserrat text-2xl font-extrabold text-brand-primary sm:text-3xl">Carga Masiva de Provincia</h1>
          <p className="mt-2 font-montserrat text-sm text-slate-600">Carga, valida y crea entregas de provincia con trazabilidad por fila.</p>
        </div>

        {error && (
          <div className="flex items-start gap-3 rounded-xl border-l-4 border-red-500 bg-red-50 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
            <p className="font-montserrat text-sm text-red-700">{error}</p>
          </div>
        )}

        {loadingCatalogs && currentStep === 'date' && (
          <div className="rounded-xl bg-white p-4 font-montserrat text-sm text-slate-500">Cargando provincias y productos...</div>
        )}

        {currentStep === 'date' && (
          <DateSelector
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            title="Paso 1: Fecha reservada del servicio"
            description="Selecciona la fecha que reservaste para este lote de provincia"
            selectedLabel="Fecha reservada:"
            batchHelp="Todas las entregas de provincia usarán la misma fecha reservada"
          />
        )}

        {selectedDate && ['upload', 'validating', 'review'].includes(currentStep) && (
          <ProvinceFileUploader
            selectedFile={selectedFile}
            onFileSelect={handleFileSelect}
            onClearFile={clearFile}
            disabled={currentStep === 'validating' || loadingCatalogs || provinces.length === 0}
            provinces={provinces}
            userProducts={userProducts}
          />
        )}

        {currentStep === 'validating' && (
          <ValidationProgress totalRows={validationProgress.total} processedRows={validationProgress.processed} />
        )}

        {currentStep === 'review' && deliveries.length > 0 && (
          <ProvinceDeliveriesReview
            deliveries={deliveries}
            selectedDate={selectedDate}
            provinces={provinces}
            userProducts={userProducts}
            onUpdate={updateDelivery}
            onDelete={deleteDelivery}
            onProceed={submitBatch}
            onChangeDate={() => setCurrentStep('date')}
          />
        )}

        {currentStep === 'submitting' && <AtomicSubmissionProgress total={deliveries.length} />}

        {currentStep === 'complete' && submissionResult && (
          <section className="rounded-2xl border-2 border-brand-green bg-white p-6 text-center sm:p-8">
            <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-brand-green to-brand-primary"><PackageCheck className="h-10 w-10 text-white" /></div>
            <h2 className="font-montserrat text-2xl font-extrabold text-brand-green">Lote creado correctamente</h2>
            <p className="mt-2 font-montserrat text-sm text-slate-600">{submissionResult.created} entregas de provincia fueron confirmadas atómicamente.</p>
            <div className="mx-auto mt-6 max-w-md">
              <div className="h-3 overflow-hidden rounded-full bg-slate-200" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="100"><div className="h-full w-full rounded-full bg-brand-green" /></div>
              <p className="mt-2 font-montserrat text-xs font-semibold text-brand-green">100% completado</p>
              <p className="mt-3 break-all font-montserrat text-xs text-slate-500">Lote: {submissionResult.batch_id}</p>
            </div>
            <div className="mx-auto mt-6 flex max-w-md flex-col gap-3">
              <button type="button" onClick={() => setActiveComponent('Analytics')} className="flex items-center justify-center gap-2 rounded-xl bg-brand-primary px-4 py-3 font-montserrat font-semibold text-white"><CheckCircle className="h-5 w-5" />Ver mis entregas</button>
              <button type="button" onClick={reset} className="flex items-center justify-center gap-2 rounded-xl border-2 border-slate-300 px-4 py-3 font-montserrat font-semibold text-slate-700"><RotateCcw className="h-5 w-5" />Crear otro lote</button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

export default ProvinciaMasivoWizard;
